/**
 * Save & Export - Save creature and download/share
 * Ages 4-6, Simple save and download functionality
 */

import { useState } from 'react';
import { motion } from 'framer-motion';
import { CreatureType, SuperpowerCard } from './types';
import { CREATURE_CARDS } from './data';

interface SaveExportProps {
  language: 'en' | 'es';
  creature: CreatureType;
  creatureName: string;
  selectedPowers: SuperpowerCard[];
  drawingDataURL: string;
  onComplete: () => void;
}

const TEXT = {
  en: {
    title: 'Your Creature is Ready!',
    subtitle: 'Download your creation and share with friends',
    name: 'Name',
    powers: 'Superpowers',
    download: 'Download Picture',
    continue: 'Continue to Reflection',
    downloading: 'Downloading...'
  },
  es: {
    title: '¡Tu Criatura está Lista!',
    subtitle: 'Descarga tu creación y comparte con amigos',
    name: 'Nombre',
    powers: 'Superpoderes',
    download: 'Descargar Imagen',
    continue: 'Continuar a Reflexión',
    downloading: 'Descargando...'
  }
};

export default function SaveExport({
  language,
  creature,
  creatureName,
  selectedPowers,
  drawingDataURL,
  onComplete
}: SaveExportProps) {
  const [isDownloading, setIsDownloading] = useState(false);

  const t = TEXT[language];
  const creatureData = CREATURE_CARDS.find(c => c.type === creature)!;

  // Download as PNG
  const handleDownload = () => {
    setIsDownloading(true);

    try {
      const link = document.createElement('a');
      link.download = `${creatureName}-creature.png`;
      link.href = drawingDataURL;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Failed to download:', error);
    } finally {
      setTimeout(() => setIsDownloading(false), 1000);
    }
  };

  return (
    <div className="min-h-[calc(100vh-80px)] flex flex-col items-center justify-center p-6">
      <div className="max-w-4xl w-full">
        {/* Header */}
        <motion.div
          initial={{ y: -30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="text-center mb-8"
        >
          <motion.div
            animate={{
              scale: [1, 1.2, 1],
              rotate: [0, 10, -10, 0]
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: 'easeInOut'
            }}
            className="text-9xl mb-4"
          >
            {creatureData.emoji}
          </motion.div>

          <h2
            className="text-5xl md:text-7xl mb-2"
            style={{
              fontFamily: "'Fraunces', Georgia, serif",
              fontWeight: 300,
              color: '#2a6d5b',
              letterSpacing: '-0.015em',
            }}
          >
            {t.title}
          </h2>

          <p
            className="text-2xl md:text-3xl"
            style={{
              fontFamily: "'Fraunces', Georgia, serif",
              fontStyle: 'italic',
              fontWeight: 500,
              color: '#2a6d5b',
            }}
          >
            {t.subtitle}
          </p>
        </motion.div>

        {/* Creature Card */}
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="p-8 mb-8"
          style={{ background: '#ffffff', border: '1px solid #e8e0c8', borderRadius: 24 }}
        >
          <div className="flex flex-col md:flex-row gap-8">
            {/* Drawing */}
            <div className="flex-1">
              <img
                src={drawingDataURL}
                alt={creatureName}
                style={{
                  width: '100%',
                  height: 'auto',
                  borderRadius: 18,
                  border: '1px solid #e8e0c8',
                  background: '#fbfbe8',
                }}
              />
            </div>

            {/* Info */}
            <div className="flex-1">
              <div className="mb-6">
                <p
                  style={{
                    fontFamily: "'Inter', system-ui, sans-serif",
                    fontSize: 13,
                    fontWeight: 600,
                    letterSpacing: '0.18em',
                    textTransform: 'uppercase',
                    color: '#9a9a9a',
                    marginBottom: 6,
                  }}
                >
                  {t.name}
                </p>
                <h3
                  style={{
                    fontFamily: "'Fraunces', Georgia, serif",
                    fontStyle: 'italic',
                    fontSize: 48,
                    fontWeight: 600,
                    color: '#2a6d5b',
                  }}
                >
                  {creatureName}
                </h3>
              </div>

              <div className="mb-6">
                <p
                  style={{
                    fontFamily: "'Inter', system-ui, sans-serif",
                    fontSize: 13,
                    fontWeight: 600,
                    letterSpacing: '0.18em',
                    textTransform: 'uppercase',
                    color: '#9a9a9a',
                    marginBottom: 12,
                  }}
                >
                  {t.powers}
                </p>
                <div className="space-y-2">
                  {selectedPowers.map(power => (
                    <div
                      key={power.id}
                      className="flex items-center gap-3 p-3"
                      style={{
                        background: '#f4efde',
                        borderRadius: 14,
                      }}
                    >
                      <span style={{ fontSize: 40, lineHeight: 1 }}>{power.icon}</span>
                      <span
                        style={{
                          fontFamily: "'Fraunces', Georgia, serif",
                          fontStyle: 'italic',
                          fontSize: 22,
                          fontWeight: 600,
                          color: '#2a6d5b',
                        }}
                      >
                        {power.name[language]}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Download Button — outline secondary */}
        <div className="text-center mb-6">
          <motion.button
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4 }}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleDownload}
            disabled={isDownloading}
            style={{
              padding: '14px 32px',
              background: 'transparent',
              color: isDownloading ? '#9a9a9a' : '#42a57a',
              border: `1.5px solid ${isDownloading ? '#9a9a9a' : '#42a57a'}`,
              borderRadius: 999,
              fontFamily: "'Inter', system-ui, sans-serif",
              fontSize: 20,
              fontWeight: 600,
              cursor: isDownloading ? 'not-allowed' : 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 12,
            }}
          >
            {isDownloading ? (
              <>
                <motion.span
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                  style={{ display: 'inline-block' }}
                >
                  ⟳
                </motion.span>
                {t.downloading}
              </>
            ) : (
              t.download
            )}
          </motion.button>
        </div>

        {/* Continue Button — primary brand-green */}
        <div className="text-center">
          <motion.button
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.6 }}
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.97 }}
            onClick={onComplete}
            style={{
              padding: '18px 44px',
              background: '#42a57a',
              color: '#ffffff',
              fontFamily: "'Inter', system-ui, sans-serif",
              fontSize: 24,
              fontWeight: 600,
              borderRadius: 999,
              border: 'none',
              cursor: 'pointer',
              boxShadow: '0 6px 22px rgba(66,165,122,0.32)',
            }}
          >
            {t.continue} →
          </motion.button>
        </div>
      </div>
    </div>
  );
}
